import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import { notify } from "../../../feedback/SnackbarProvider";
import { Alert } from "../../Alert/Alert";
import { Button } from "../../Button/Button";
import { Input } from "../../Input/Input";
import { List } from "../../List/List";
import { Popconfirm } from "../../Overlay/Overlay";
import { Switch } from "../../Switch/Switch";
import { Tabs } from "../../Tabs/Tabs";
import { Tag } from "../../Tag/Tag";
import { Tooltip } from "../../Tooltip/Tooltip";
import { Typography } from "../../Typography/Typography";
import { Modal } from "../../Modal/Modal";
import { Form } from "../../Form/Form";
import { Select } from "../../Select/Select";
import { AccountTreeOutlined as BranchesOutlined, DeleteOutline as DeleteOutlined, EditOutlined as EditOutlined, GroupOutlined as GroupAccessOutlined, History as HistoryOutlined, Add as PlusOutlined, Search as SearchOutlined, VisibilityOffOutlined as DeactivateOutlined } from "@mui/icons-material";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";
import { useOptionalCurrentUser } from "../../../utils/hooks";
import {
  MY_ACTIVE_CHAT_SKILLS,
  AVAILABLE_ORG_CHAT_SKILLS,
  CREATE_CHAT_SKILL,
  UPDATE_CHAT_SKILL,
  DELETE_CHAT_SKILL,
  OPT_IN_TO_CHAT_SKILL,
  OPT_OUT_OF_CHAT_SKILL,
  CHAT_SKILL_HISTORY,
  CHAT_SKILL_GROUP_ACCESS,
  GRANT_CHAT_SKILL_GROUP_ACCESS,
  REVOKE_CHAT_SKILL_GROUP_ACCESS,
  ORGANIZATION_GROUPS,
  SYSTEM_CHAT_SKILLS,
} from "../graphql";
import { ChatSkillScope } from "_graphql-types-frontend/graphql";
import { SchedulesTab } from "./SchedulesTab";

function extractGqlMessage(error: unknown): string {
  const e = error as any;
  return e?.graphQLErrors?.[0]?.message ?? e?.message ?? "An unexpected error occurred.";
}

const MY_SKILLS = MY_ACTIVE_CHAT_SKILLS;
const ORG_SKILLS = AVAILABLE_ORG_CHAT_SKILLS;
const CREATE = CREATE_CHAT_SKILL;
const UPDATE = UPDATE_CHAT_SKILL;
const DELETE = DELETE_CHAT_SKILL;
const OPT_IN = OPT_IN_TO_CHAT_SKILL;
const OPT_OUT = OPT_OUT_OF_CHAT_SKILL;
const GRANT_GROUP_ACCESS = GRANT_CHAT_SKILL_GROUP_ACCESS;
const REVOKE_GROUP_ACCESS = REVOKE_CHAT_SKILL_GROUP_ACCESS;

const { Text } = Typography;

type ChatSkill = {
  id: number;
  name: string;
  description: string;
  instructions: string;
  isAutoInvocable: boolean;
  scope: ChatSkillScope;
  organizationId: number | null;
  createUser: string;
  createUserFullName: string | null;
  // Server-computed rights. `canEdit` is deliberately NOT `createUser ===
  // me`: the owner of an org skill can grant write access to a group, so
  // ownership no longer implies the rights and the rights no longer imply
  // ownership. Both come straight from ChatSkillMutator.isEntitled, so the UI
  // never re-derives what the mutator enforces.
  canEdit: boolean;
  // Narrower than canEdit on org skills: deleting removes the skill for the
  // whole organization, and only the owner (or a super user) may do that — or
  // hand write access to another group.
  canDelete: boolean;
};

type ChatSkillGroupAccess = {
  id: number;
  chatSkillId: number;
  groupId: number;
  access: string;
  groupName: string | null;
};

// The picker's candidates come back as a two-scalar type, not `User` rows —
// see ChatSkillGrantableGroup on the server for why.
type GrantableGroup = {
  id: number;
  name: string;
};

type SkillHistoryEntry = {
  id: number;
  version: number;
  name: string;
  description: string;
  instructions: string;
  archivedAt: string;
  archivedBy: string;
  archivedByFullName: string | null;
};

type SkillFormValues = {
  name: string;
  description: string;
  instructions: string;
  isAutoInvocable: boolean;
  scope: ChatSkillScope;
};

function ScopeTag({ scope }: { scope: ChatSkillScope }) {
  const color =
    scope === ChatSkillScope.System
      ? "blue"
      : scope === ChatSkillScope.Organization
        ? "green"
        : "default";
  return (
    <Tag
      color={color}
      style={{
        fontSize: 10,
        lineHeight: "14px",
        margin: 0,
        padding: "1px 4px",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {scope.toLowerCase()}
    </Tag>
  );
}

function SkillFormModal({
  open,
  onCancel,
  onSubmit,
  loading,
  initial,
  organizationId,
  title,
  warning,
  sourceName,
  sourceDescription,
  ownerOnlyFields = true,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: SkillFormValues) => void;
  loading: boolean;
  initial?: Partial<SkillFormValues>;
  organizationId: number | null;
  title?: string;
  warning?: string;
  sourceName?: string;
  sourceDescription?: string;
  // Scope and Auto-invoke act on the whole organization, so the server keeps
  // them with the owner even though a group grantee may edit the content
  // (ChatSkillMutator.updateSkill). Rendering them live for a grantee invites a
  // save that is rejected outright — and the rejection takes their instruction
  // edit down with it, with nothing in the form saying which control was not
  // theirs. Defaults to true: creating a skill makes you its owner.
  ownerOnlyFields?: boolean;
}) {
  const [form] = Form.useForm<SkillFormValues>();
  const scope = Form.useWatch("scope", form);

  return (
    <Modal
      title={title ?? (initial ? "Edit Skill" : "New Skill")}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      destroyOnClose
      width="min(90vw, 900px)"
    >
      {warning && (
        <Alert
          type="info"
          message={warning}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          scope: ChatSkillScope.Personal,
          isAutoInvocable: false,
          ...initial,
        }}
        onFinish={onSubmit}
        size="small"
      >
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Item
            name="name"
            label="Name"
            style={{ flex: 2 }}
            rules={[
              { required: true, whitespace: true },
              { max: 50, message: "Name must be 50 characters or fewer" },
              {
                pattern: /^[a-zA-Z0-9_-]+$/,
                message: "Only letters, numbers, - and _ allowed",
              },
              ...(sourceName
                ? [
                    {
                      validator: (_: unknown, value: string) =>
                        value !== sourceName
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "Rename the skill to distinguish it from the original"
                              )
                            ),
                    },
                  ]
                : []),
            ]}
          >
            <Input
              placeholder="e.g. fund-comparison"
              maxLength={50}
              showCount
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="scope"
            label="Scope"
            style={{ flex: 1 }}
            extra={
              ownerOnlyFields ? undefined : "Only the skill's owner can change this"
            }
          >
            <Select
              style={{ width: "100%" }}
              disabled={!ownerOnlyFields}
              data-testid="skill-scope-select"
            >
              <Select.Option value="PERSONAL">Personal</Select.Option>
              {organizationId && (
                <Select.Option value="ORGANIZATION">Organization</Select.Option>
              )}
            </Select>
          </Form.Item>
        </div>
        {scope === ChatSkillScope.Organization && (
          <Alert
            type="info"
            message="Organization skills are active for everyone in your organization as soon as you save. Each person can deactivate it for themselves."
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, whitespace: true },
            ...(sourceDescription
              ? [
                  {
                    validator: (_: unknown, value: string) =>
                      value !== sourceDescription
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Update the description to make it your own"
                            )
                          ),
                  },
                ]
              : []),
          ]}
        >
          <Input.TextArea
            rows={2}
            placeholder="Short description of what this skill does"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item
          name="instructions"
          label="Instructions"
          rules={[{ required: true, whitespace: true }]}
          extra="Prompt instructions injected when this skill is activated"
        >
          <Input.TextArea
            rows={12}
            placeholder="Use DocumentsRagTool to find relevant documents, then..."
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item
          name="isAutoInvocable"
          label="Auto-invoke"
          valuePropName="checked"
          extra={
            ownerOnlyFields
              ? "When enabled, the AI can activate this skill automatically"
              : "Only the skill's owner can change auto-invocation"
          }
        >
          <Switch
            size="small"
            disabled={!ownerOnlyFields}
            data-testid="skill-auto-invoke-switch"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function SkillHistoryModal({
  skill,
  onClose,
}: {
  skill: ChatSkill | null;
  onClose: () => void;
}) {
  const [selectedVersion, setSelectedVersion] =
    useState<SkillHistoryEntry | null>(null);

  const { data, loading } = useQuery(CHAT_SKILL_HISTORY, {
    variables: { id: skill?.id ?? 0 },
    skip: !skill,
    onCompleted: data => {
      const entries: SkillHistoryEntry[] =
        (data as any)?.chatSkillHistory ?? [];
      if (entries.length > 0) setSelectedVersion(entries[0]);
    },
  });

  const history: SkillHistoryEntry[] = (data as any)?.chatSkillHistory ?? [];

  return (
    <Modal
      title={skill ? `Version History — /${skill.name}` : "Version History"}
      open={!!skill}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      afterClose={() => setSelectedVersion(null)}
    >
      {loading && <CircularProgress size={16} />}
      {!loading && history.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No version history yet. History is recorded each time a skill is
          updated.
        </Text>
      )}
      {!loading && history.length > 0 && (
        <div style={{ display: "flex", gap: 16, minHeight: 400 }}>
          <div
            style={{
              width: 200,
              flexShrink: 0,
              borderRight: "1px solid #f0f0f0",
              paddingRight: 12,
            }}
          >
            <Text
              type="secondary"
              style={{ fontSize: 11, display: "block", marginBottom: 6 }}
            >
              {history.length} saved version{history.length !== 1 ? "s" : ""}
            </Text>
            <List
              size="small"
              dataSource={history}
              renderItem={(entry: SkillHistoryEntry) => (
                <List.Item
                  style={{
                    padding: "4px 6px",
                    cursor: "pointer",
                    borderRadius: 4,
                    background:
                      selectedVersion?.id === entry.id
                        ? "#e6f4ff"
                        : "transparent",
                  }}
                  onClick={() => setSelectedVersion(entry)}
                >
                  <div>
                    <Text style={{ fontSize: 12, fontWeight: 500 }}>
                      v{entry.version}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 10, display: "block" }}
                    >
                      {new Date(entry.archivedAt).toLocaleString()}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 10, display: "block" }}
                      ellipsis
                    >
                      {entry.archivedByFullName || entry.archivedBy}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </div>
          <div style={{ flex: 1, overflow: "auto" }}>
            {selectedVersion && skill ? (
              <>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", marginBottom: 8 }}
                >
                  Comparing v{selectedVersion.version} (left) to current (right)
                </Text>
                <ReactDiffViewer
                  oldValue={selectedVersion.instructions}
                  newValue={skill.instructions}
                  splitView
                  compareMethod={DiffMethod.WORDS}
                  leftTitle={`v${selectedVersion.version}`}
                  rightTitle="Current"
                  styles={{
                    contentText: {
                      fontFamily: "monospace",
                      fontSize: "11px",
                    },
                  }}
                />
              </>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Select a version to compare.
              </Text>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// All mutations refresh both queries so the My Skills and Org Skills tabs
// stay in sync without manual reload. `awaitRefetchQueries: true` keeps the
// loading state on the trigger until the refetch completes, avoiding a
// flash of stale data. Operation names refetch all active observers
// regardless of variables, so the parameterized AvailableOrgChatSkills
// query is correctly refreshed even though we don't know its variables here.
const REFETCH_BOTH = {
  refetchQueries: ["MyActiveChatSkills", "AvailableOrgChatSkills"],
  awaitRefetchQueries: true,
};

/**
 * Hand write access to an organization skill to a group, or take it back.
 *
 * Assignment, not transfer: the owner stays the owner (and keeps the sole right
 * to delete and to re-share). Everyone in the chosen group — including through
 * the group hierarchy — can edit the skill and read its history.
 */
function SkillAccessModal({
  skill,
  onClose,
}: {
  skill: ChatSkill | null;
  onClose: () => void;
}) {
  // The skill's organization, not the caller's. `assertGrantableGroup`
  // validates the chosen group against `skill.organizationId`, and canDelete —
  // which gates this panel — is true for a super user on any organization's
  // skill. Keying the picker off the caller would offer the wrong
  // organization's groups and every grant would be refused.
  const organizationId = skill?.organizationId ?? null;
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  const {
    data,
    loading,
    error: grantsError,
    refetch,
  } = useQuery(CHAT_SKILL_GROUP_ACCESS, {
    variables: { chatSkillId: skill?.id ?? 0 },
    skip: !skill,
    fetchPolicy: "cache-and-network",
  });

  // The picker is a convenience, not the gate: the server re-validates the
  // chosen group on grant regardless. Surface a lookup failure rather than
  // showing a silently empty dropdown — existing grants still render, and
  // revoking still works.
  const { data: groupData, error: groupError } = useQuery(ORGANIZATION_GROUPS, {
    variables: { organizationId: organizationId!, q: groupSearch || null },
    skip: !skill || !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const [grantAccess, { loading: granting }] = useMutation(GRANT_GROUP_ACCESS);
  const [revokeAccess] = useMutation(REVOKE_GROUP_ACCESS);

  const grants: ChatSkillGroupAccess[] =
    (data as any)?.chatSkillGroupAccess ?? [];
  const groups: GrantableGroup[] =
    (groupData as any)?.chatSkillGrantableGroups ?? [];
  const grantedIds = new Set(grants.map(g => g.groupId));

  const handleGrant = async () => {
    if (!skill || selectedGroupId == null) return;
    try {
      await grantAccess({
        variables: { chatSkillId: skill.id, groupId: selectedGroupId },
      });
      setSelectedGroupId(null);
      await refetch();
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  const handleRevoke = async (groupId: number) => {
    if (!skill) return;
    try {
      await revokeAccess({ variables: { chatSkillId: skill.id, groupId } });
      await refetch();
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  return (
    <Modal
      title={skill ? `Who can edit /${skill.name}` : "Skill access"}
      open={!!skill}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      afterClose={() => {
        setGroupSearch("");
        setSelectedGroupId(null);
      }}
      width="min(90vw, 560px)"
    >
      <Alert
        type="info"
        message="Members of these groups can edit this skill and see its version history. Only you can delete it or change who has access."
        style={{ marginBottom: 16 }}
        showIcon
      />
      {groupError && (
        <Alert
          type="warning"
          message="Could not load your organization's groups, so the picker below is empty. Retry, or ask an administrator to grant access on your behalf."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Select
          showSearch
          allowClear
          style={{ flex: 1 }}
          size="small"
          placeholder="Search groups in your organization..."
          value={selectedGroupId ?? undefined}
          onSearch={(value: string) => setGroupSearch(value)}
          onChange={(value: unknown) =>
            setSelectedGroupId(value == null ? null : Number(value))
          }
          filterOption={false}
          data-testid="skill-access-group-select"
        >
          {groups
            .filter(group => !grantedIds.has(group.id))
            .map(group => (
              <Select.Option key={group.id} value={group.id}>
                {group.name}
              </Select.Option>
            ))}
        </Select>
        <Button
          size="small"
          type="primary"
          disabled={selectedGroupId == null}
          loading={granting}
          onClick={handleGrant}
        >
          Add
        </Button>
      </div>
      {loading && grants.length === 0 && <CircularProgress size={16} />}
      {/* A failed lookup must not read as "nobody has access" — that is the one
          wrong answer here, since it invites granting access that already
          exists, or assuming none was ever given. */}
      {grantsError && !loading ? (
        <Alert
          type="error"
          message="Could not load who has access to this skill. Retry before changing anything — the list below may be incomplete."
          style={{ marginBottom: 8 }}
          showIcon
        />
      ) : null}
      {!loading && !grantsError && grants.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No groups have write access yet.
        </Text>
      ) : (
        <List
          size="small"
          dataSource={grants}
          renderItem={(grant: ChatSkillGroupAccess) => (
            <List.Item style={{ padding: "4px 0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 8,
                }}
              >
                <Text ellipsis style={{ fontSize: 12, flex: 1 }}>
                  {grant.groupName || `Group ${grant.groupId}`}
                </Text>
                <Tag style={{ fontSize: 9, margin: 0, padding: "1px 3px" }}>
                  {grant.access}
                </Tag>
                <Popconfirm
                  title="Remove this group's write access?"
                  onConfirm={() => handleRevoke(grant.groupId)}
                  okText="Remove"
                  cancelText="Cancel"
                >
                  <DeleteOutlined
                    style={{ fontSize: 12, color: "#999", cursor: "pointer" }}
                    data-testid={`revoke-group-access-${grant.groupId}`}
                  />
                </Popconfirm>
              </div>
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
}

// Shared by both tabs: you can reach a skill you may write from My Skills (when
// it is active for you) or from the org catalog (always). Keyed per skill so the
// antd Form store is remounted fresh for each edit target instead of keeping
// stale values.
function SkillEditModal({
  editing,
  onClose,
  organizationId,
}: {
  editing: ChatSkill | null;
  onClose: () => void;
  organizationId: number | null;
}) {
  const [updateSkill, { loading }] = useMutation(UPDATE, REFETCH_BOTH);

  // canDelete is exactly "owner or super user" on an org skill — the same set
  // the server lets change scope and auto-invocation. A grantee's save carries
  // neither field rather than echoing values they cannot change, so a rejected
  // owner-only field can never cost them their instruction edit.
  const ownerOnlyFields = editing?.canDelete ?? true;

  const handleUpdate = async (values: SkillFormValues) => {
    if (!editing) return;
    try {
      await updateSkill({
        variables: {
          id: editing.id,
          input: {
            name: values.name,
            description: values.description,
            instructions: values.instructions,
            ...(ownerOnlyFields
              ? {
                  isAutoInvocable: values.isAutoInvocable,
                  scope: values.scope,
                }
              : {}),
          },
        },
      });
      onClose();
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  return (
    <SkillFormModal
      key={editing?.id ?? "edit"}
      ownerOnlyFields={ownerOnlyFields}
      open={!!editing}
      onCancel={onClose}
      onSubmit={handleUpdate}
      loading={loading}
      initial={
        editing
          ? {
              name: editing.name,
              description: editing.description,
              instructions: editing.instructions,
              isAutoInvocable: editing.isAutoInvocable,
              scope: editing.scope as ChatSkillScope,
            }
          : undefined
      }
      organizationId={organizationId}
    />
  );
}

function MySkillsList() {
  // Non-throwing: during a transient empty current-user cache this renders the
  // list without edit affordances instead of crashing the whole chat page.
  const currentUser = useOptionalCurrentUser();
  const { data, loading } = useQuery(MY_SKILLS, {
    fetchPolicy: "cache-and-network",
  });
  const [deleteSkill] = useMutation(DELETE, REFETCH_BOTH);
  const [optOut] = useMutation(OPT_OUT, REFETCH_BOTH);
  const [createSkill, { loading: creating }] = useMutation(
    CREATE,
    REFETCH_BOTH
  );
  const [updateSkill, { loading: updating }] = useMutation(
    UPDATE,
    REFETCH_BOTH
  );
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChatSkill | null>(null);
  const [viewingHistory, setViewingHistory] = useState<ChatSkill | null>(null);
  const [managingAccess, setManagingAccess] = useState<ChatSkill | null>(null);

  if (loading && !data) return <CircularProgress size={16} />;

  const skills: ChatSkill[] = ((data as any)?.myActiveChatSkills ?? []).filter(
    (s: ChatSkill) => s.scope !== ChatSkillScope.System
  );

  // Write rights come from the server (ChatSkillMutator.isEntitled), not from
  // comparing createUser: a group grantee can edit a skill they do not own, and
  // an owner keeps edit rights on a skill they have deactivated for themselves.
  const canEdit = (skill: ChatSkill) =>
    skill.scope !== ChatSkillScope.System && skill.canEdit;

  // Deleting an org skill destroys it for everyone in the organization, and
  // handing write access to another group is the same right — both are the
  // owner's (or a super user's), which is exactly what canDelete reports.
  const canManageAccess = (skill: ChatSkill) =>
    skill.scope === ChatSkillScope.Organization && skill.canDelete;

  // Organization placement is not part of the write inputs — the server
  // derives it from the current user for organization-scoped skills.
  const handleCreate = async (values: SkillFormValues) => {
    try {
      await createSkill({
        variables: {
          input: {
            name: values.name,
            description: values.description,
            instructions: values.instructions,
            isAutoInvocable: values.isAutoInvocable,
            scope: values.scope,
          },
        },
      });
      setFormOpen(false);
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  // Same rule as SkillEditModal: scope and auto-invocation are the owner's, and
  // canDelete is that set. A grantee reaching their skill from My Skills must
  // get the same form as one reaching it from the org catalog.
  const ownerOnlyFields = editing?.canDelete ?? true;

  const handleUpdate = async (values: SkillFormValues) => {
    if (!editing) return;
    try {
      await updateSkill({
        variables: {
          id: editing.id,
          input: {
            name: values.name,
            description: values.description,
            instructions: values.instructions,
            ...(ownerOnlyFields
              ? {
                  isAutoInvocable: values.isAutoInvocable,
                  scope: values.scope,
                }
              : {}),
          },
        },
      });
      setEditing(null);
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  // Deactivate and delete are deliberately separate controls. Org skills are on
  // by default for everyone, so the destructive action must never be what a
  // user reaches for when they only want the skill out of their own list.
  const handleDeactivate = async (skill: ChatSkill) => {
    try {
      await optOut({ variables: { chatSkillId: skill.id } });
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  const handleDelete = async (skill: ChatSkill) => {
    try {
      await deleteSkill({ variables: { id: skill.id } });
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>
          {skills.length} active skill{skills.length !== 1 ? "s" : ""}
        </Text>
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onClick={() => setFormOpen(true)}
        >
          New
        </Button>
      </div>
      {skills.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No skills yet. Create one or browse your organization's skills.
        </Text>
      ) : (
        <List
          size="small"
          dataSource={skills}
          renderItem={(skill: ChatSkill) => (
            <List.Item style={{ padding: "4px 0" }}>
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Text
                    ellipsis
                    style={{ fontSize: 12, fontWeight: 500, flex: 1 }}
                  >
                    /{skill.name}
                  </Text>
                  <ScopeTag scope={skill.scope} />
                  {skill.isAutoInvocable && (
                    <Tag
                      style={{
                        fontSize: 9,
                        lineHeight: "12px",
                        margin: 0,
                        padding: "1px 3px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      auto
                    </Tag>
                  )}
                  {canEdit(skill) && (
                    <>
                      <EditOutlined
                        onClick={() => setEditing(skill)}
                        style={{
                          fontSize: 12,
                          color: "#999",
                          cursor: "pointer",
                        }}
                      />
                      <Tooltip title="Version history">
                        <HistoryOutlined
                          onClick={() => setViewingHistory(skill)}
                          style={{
                            fontSize: 12,
                            color: "#999",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                  {canManageAccess(skill) && (
                    <Tooltip title="Manage group access">
                      <GroupAccessOutlined
                        onClick={() => setManagingAccess(skill)}
                        style={{
                          fontSize: 12,
                          color: "#999",
                          cursor: "pointer",
                        }}
                        data-testid={`manage-skill-access-${skill.id}`}
                      />
                    </Tooltip>
                  )}
                  {skill.scope === ChatSkillScope.Organization && (
                    <Popconfirm
                      title={`Deactivate /${skill.name} for you? It stays active for everyone else in your organization.`}
                      onConfirm={() => handleDeactivate(skill)}
                      okText="Deactivate"
                      cancelText="Cancel"
                    >
                      <Tooltip title="Deactivate for me">
                        <DeactivateOutlined
                          style={{
                            fontSize: 12,
                            color: "#999",
                            cursor: "pointer",
                          }}
                          data-testid={`deactivate-skill-${skill.id}`}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                  {skill.canDelete && (
                    <Popconfirm
                      title={
                        skill.scope === ChatSkillScope.Organization
                          ? `Delete /${skill.name} for the whole organization? Every user in your organization loses this skill. This cannot be undone.`
                          : `Delete /${skill.name}?`
                      }
                      onConfirm={() => handleDelete(skill)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Tooltip
                        title={
                          skill.scope === ChatSkillScope.Organization
                            ? "Delete for the whole organization"
                            : "Delete skill"
                        }
                      >
                        <DeleteOutlined
                          style={{
                            fontSize: 12,
                            color: "#999",
                            cursor: "pointer",
                          }}
                          data-testid={`delete-skill-${skill.id}`}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </div>
                <Text
                  type="secondary"
                  ellipsis
                  style={{ fontSize: 10, display: "block" }}
                >
                  {skill.description}
                </Text>
              </div>
            </List.Item>
          )}
        />
      )}
      <SkillFormModal
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onSubmit={handleCreate}
        loading={creating}
        organizationId={currentUser?.organizationId ?? null}
      />
      <SkillFormModal
        key={editing?.id ?? "edit"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onSubmit={handleUpdate}
        loading={updating}
        ownerOnlyFields={ownerOnlyFields}
        initial={
          editing
            ? {
                name: editing.name,
                description: editing.description,
                instructions: editing.instructions,
                isAutoInvocable: editing.isAutoInvocable,
                scope: editing.scope as ChatSkillScope,
              }
            : undefined
        }
        organizationId={currentUser?.organizationId ?? null}
      />
      <SkillHistoryModal
        skill={viewingHistory}
        onClose={() => setViewingHistory(null)}
      />
      <SkillAccessModal
        skill={managingAccess}
        onClose={() => setManagingAccess(null)}
      />
    </div>
  );
}

function OrgSkillsBrowser() {
  const currentUser = useOptionalCurrentUser();
  const organizationId = currentUser?.organizationId;

  const { data: activeData } = useQuery(MY_SKILLS, {
    fetchPolicy: "cache-and-network",
  });

  const { data, loading } = useQuery(ORG_SKILLS, {
    variables: { organizationId: organizationId! },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const [optIn] = useMutation(OPT_IN, REFETCH_BOTH);
  const [optOut] = useMutation(OPT_OUT, REFETCH_BOTH);
  const [createSkill, { loading: forking }] = useMutation(CREATE, REFETCH_BOTH);
  const [forkSource, setForkSource] = useState<ChatSkill | null>(null);
  const [editing, setEditing] = useState<ChatSkill | null>(null);
  const [viewingHistory, setViewingHistory] = useState<ChatSkill | null>(null);
  const [managingAccess, setManagingAccess] = useState<ChatSkill | null>(null);
  const [searchText, setSearchText] = useState("");
  // Track skills currently mid-toggle so we can disable the Switch and show
  // a loading indicator without affecting other rows.
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

  // See handleCreate: the input is built allowlist-style and carries no
  // organization — the server derives placement from the current user.
  const handleFork = async (values: SkillFormValues) => {
    try {
      await createSkill({
        variables: {
          input: {
            name: values.name,
            description: values.description,
            instructions: values.instructions,
            isAutoInvocable: values.isAutoInvocable,
            scope: values.scope,
          },
        },
      });
      setForkSource(null);
    } catch (e) {
      notify({ severity: "error", message: extractGqlMessage(e) });
    }
  };

  if (!organizationId) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No organization found.
      </Text>
    );
  }

  if (loading && !data) return <CircularProgress size={16} />;

  const orgSkills: ChatSkill[] = (data as any)?.availableOrgChatSkills ?? [];
  const activeSkillIds = new Set<number>(
    ((activeData as any)?.myActiveChatSkills ?? []).map((s: ChatSkill) => s.id)
  );

  const filtered = orgSkills.filter(
    s =>
      s.name.toLowerCase().includes(searchText.toLowerCase()) ||
      s.description.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleToggle = async (skill: ChatSkill, nextActive: boolean) => {
    setPendingIds(prev => {
      const next = new Set(prev);
      next.add(skill.id);
      return next;
    });
    try {
      if (nextActive) {
        await optIn({ variables: { chatSkillId: skill.id } });
      } else {
        await optOut({ variables: { chatSkillId: skill.id } });
      }
    } catch (e) {
      // Both directions validate scope and organization server-side now, so a
      // toggle can fail. Surface it instead of leaving an unhandled rejection
      // and a switch that silently springs back.
      notify({ severity: "error", message: extractGqlMessage(e) });
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(skill.id);
        return next;
      });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Input
        size="small"
        prefix={<SearchOutlined style={{ color: "#999" }} />}
        placeholder="Search org skills..."
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        allowClear
        style={{ width: "100%", marginBottom: 8 }}
      />
      {filtered.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {searchText
            ? "No matching skills found."
            : "No organization skills available."}
        </Text>
      ) : (
        <List
          size="small"
          dataSource={filtered}
          renderItem={(skill: ChatSkill) => {
            const isActive = activeSkillIds.has(skill.id);
            const isPending = pendingIds.has(skill.id);
            return (
              <List.Item style={{ padding: "4px 0" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text ellipsis style={{ fontSize: 12, fontWeight: 500 }}>
                      /{skill.name}
                    </Text>
                    <Text
                      type="secondary"
                      ellipsis
                      style={{ fontSize: 10, display: "block" }}
                    >
                      {skill.description}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: 10, color: "#bbb" }}
                    >
                      {skill.createUserFullName || skill.createUser}
                    </Text>
                  </div>
                  {/* You may write it — edit it in place; forking something you
                      can already change is noise. Edit and history live here as
                      well as in My Skills so deactivating a skill you own (or
                      were granted) never strands it in a list with no controls.
                      `canEdit` comes from the server, so a group grantee sees
                      the same affordances the owner does. */}
                  {skill.canEdit ? (
                    <>
                      <Tooltip title="Edit skill">
                        <EditOutlined
                          onClick={() => setEditing(skill)}
                          style={{ fontSize: 13, color: "#999", cursor: "pointer" }}
                        />
                      </Tooltip>
                      <Tooltip title="Version history">
                        <HistoryOutlined
                          onClick={() => setViewingHistory(skill)}
                          style={{ fontSize: 13, color: "#999", cursor: "pointer" }}
                          data-testid={`org-skill-history-${skill.id}`}
                        />
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Fork as my own skill">
                      <BranchesOutlined
                        onClick={() => setForkSource(skill)}
                        style={{ fontSize: 13, color: "#999", cursor: "pointer" }}
                      />
                    </Tooltip>
                  )}
                  {skill.canDelete && (
                    <Tooltip title="Manage group access">
                      <GroupAccessOutlined
                        onClick={() => setManagingAccess(skill)}
                        style={{ fontSize: 13, color: "#999", cursor: "pointer" }}
                        data-testid={`org-manage-skill-access-${skill.id}`}
                      />
                    </Tooltip>
                  )}
                  <Switch
                    size="small"
                    checked={isActive}
                    loading={isPending}
                    disabled={isPending}
                    onChange={(checked: boolean) =>
                      handleToggle(skill, checked)
                    }
                    data-cy={`org-skill-toggle-${skill.id}`}
                    data-testid={`org-skill-toggle-${skill.id}`}
                  />
                </div>
              </List.Item>
            );
          }}
        />
      )}
      <SkillFormModal
        key={forkSource?.id ?? "fork"}
        open={!!forkSource}
        onCancel={() => setForkSource(null)}
        onSubmit={handleFork}
        loading={forking}
        title="Fork Skill"
        warning="This creates your own copy of the skill. Rename it so it's distinct from the original."
        sourceName={forkSource?.name}
        sourceDescription={forkSource?.description}
        initial={
          forkSource
            ? {
                name: forkSource.name,
                description: forkSource.description,
                instructions: forkSource.instructions,
                isAutoInvocable: forkSource.isAutoInvocable,
                scope: ChatSkillScope.Personal,
              }
            : undefined
        }
        organizationId={currentUser?.organizationId ?? null}
      />
      <SkillEditModal
        editing={editing}
        onClose={() => setEditing(null)}
        organizationId={currentUser?.organizationId ?? null}
      />
      <SkillHistoryModal
        skill={viewingHistory}
        onClose={() => setViewingHistory(null)}
      />
      <SkillAccessModal
        skill={managingAccess}
        onClose={() => setManagingAccess(null)}
      />
    </div>
  );
}

function SystemSkillsList() {
  const { data, loading } = useQuery(SYSTEM_CHAT_SKILLS, {
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data) return <CircularProgress size={16} />;

  const skills: ChatSkill[] = (data as any)?.chatSkillsByScope ?? [];

  if (skills.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No system skills available.
      </Text>
    );
  }

  return (
    <List
      size="small"
      dataSource={skills}
      renderItem={(skill: ChatSkill) => (
        <List.Item style={{ padding: "4px 0" }}>
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Text ellipsis style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>
                /{skill.name}
              </Text>
              {skill.isAutoInvocable && (
                <Tag
                  style={{
                    fontSize: 9,
                    lineHeight: "12px",
                    margin: 0,
                    padding: "1px 3px",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  auto
                </Tag>
              )}
            </div>
            <Tooltip title={skill.description} placement="right">
              <Text
                type="secondary"
                ellipsis
                style={{ fontSize: 10, display: "block" }}
              >
                {skill.description}
              </Text>
            </Tooltip>
          </div>
        </List.Item>
      )}
    />
  );
}

export function SkillsTab() {
  return (
    <>
      {/* The counterpart to the agents line. "Skill" reads as self-evident
          until you also have agents, at which point the two need telling
          apart — so each names the other. */}
      <div
        style={{
          fontSize: 12,
          color: "#666",
          marginBottom: 8,
          lineHeight: "17px",
        }}
      >
        A <strong>reusable set of instructions</strong> for this conversation.
        The assistant follows them here, in the chat you are already in, with
        the tools it already has. Good for a workflow you repeat and want done
        the same way each time. Reach for an <strong>agent</strong> instead
        when the work should happen in its own context and come back as a
        separate opinion.
      </div>
      <Tabs
      size="small"
      defaultActiveKey="my"
      items={[
        {
          key: "my",
          label: "My Skills",
          children: <MySkillsList />,
        },
        {
          key: "org",
          label: "Org Skills",
          children: <OrgSkillsBrowser />,
        },
        {
          key: "system",
          label: "System",
          children: <SystemSkillsList />,
        },
        {
          key: "schedules",
          label: "Schedules",
          children: <SchedulesTab />,
        },
      ]}
      />
    </>
  );
}
